import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import {
	notificationEventStreamManager,
	type EventStreamStatus,
	type NotificationEvent,
} from '@mightybot/mfe-runtime';

import type { RootState } from '../store';

// Progress payload the platform publishes in each RunNotification's `data`.
export interface RunProgressData {
	run_id?: string;
	instance_id?: string | null;
	phase?: string;
	phase_status?: string;
	progress_percent?: number;
	phases_completed?: string[];
	total_phases?: number;
	error_message?: string | null;
	error_code?: string | null;
}

export interface UseRunProgressResult {
	lastEvent: NotificationEvent<RunProgressData> | null;
	status: EventStreamStatus;
	error: string | null;
	isConnected: boolean;
}

// The RocketFee JWT carries the enterprise_id; decode it client-side so we can
// build the SSE channel without an extra round-trip. (base64url payload.)
function decodeEnterpriseId(token: string | null | undefined): string | null {
	if (!token) return null;
	const part = token.split('.')[1];
	if (!part) return null;
	try {
		const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
		const payload = JSON.parse(json) as { enterprise_id?: string };
		return payload.enterprise_id ?? null;
	} catch {
		return null;
	}
}

/**
 * Subscribe to a platform Deep-Agent run's progress over SSE.
 *
 * The statement-analyzer NATS worker re-publishes each run event onto the
 * RocketFee enterprise channel
 * `notifications.{enterprise_id}.workflow.run.{runId}`, served by
 * notification-service via `/notification/event` on this origin. The stream
 * closes itself on the terminal event.
 *
 * Uses the root-exported `notificationEventStreamManager` directly (the
 * `useEventStream` hook lives behind a package subpath not in mfe-runtime's
 * exports map).
 */
export function useRunProgress(
	runId: string | null | undefined,
	enabled = true,
): UseRunProgressResult {
	// MFE mode: the host injects the token into the mfeAuth slice. Standalone
	// (rocketfee-frontend) mode: the app stores it in localStorage under
	// `auth_token` and the mfeAuth slice is empty — fall back to that.
	const reduxToken = useSelector((s: RootState) => s.mfeAuth.accessToken);
	const accessToken =
		reduxToken ??
		(typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null);
	const enterpriseId = decodeEnterpriseId(accessToken);
	const channel =
		enterpriseId && runId
			? `notifications.${enterpriseId}.workflow.run.${runId}`
			: null;

	const [lastEvent, setLastEvent] =
		useState<NotificationEvent<RunProgressData> | null>(null);
	const [status, setStatus] = useState<EventStreamStatus>('idle');
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		setLastEvent(null);
		setError(null);

		if (!enabled || !channel || !accessToken) {
			setStatus('idle');
			return;
		}

		const unsubscribe = notificationEventStreamManager.subscribe<RunProgressData>(
			{
				baseUrl: `${window.location.origin}/notification/event`,
				channel,
				token: accessToken,
				replay: true,
				enterpriseId: enterpriseId ?? undefined,
			},
			{
				onEvent: (event) => setLastEvent(event),
				onStatusChange: (next) => setStatus(next),
				onError: (next) => setError(next),
			},
		);

		return unsubscribe;
	}, [channel, enabled, accessToken, enterpriseId]);

	return { lastEvent, status, error, isConnected: status === 'open' };
}
